import { FC, MouseEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TableBody as Body } from '@mui/material';
import dayjs from 'dayjs';

import { HeaderProperties } from '../../../../shared/uiComponents/Table/tableProperties';
import { CustomTableRow, RowTableCell } from '../../../../shared/uiComponents/Table/tableHelpers';
import { Badge, TableNoData, Text } from '../../../../shared/uiComponents';
import { InfoSvg } from '../../../../shared/Assets/svgIcons';
import { useCheckPermission } from '../../../../shared/Helpers/hooks';
import { ExpireInfo } from '../../../Dashboard/helpers';
import EditAuthorization from './EditInsuranceAuthorization';
import ActivateAuthorization from './ActivateInsuranceAuthorization';
import DeleteAuthorization from './deleteInsuranceAuthorization';
import CopyAuthorization from './CopyInsuranceAuthorization';
import { PERMISSIONS } from '../../../../App/constants';
import { findEventColor, findEventIconColor } from '../../../Calendar/Helpers/functions';

import { ActiveAuthorizationProperties } from '../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import { DispatchProperties, useSelector } from '../../../../redux/store';
import { getSessionTypes } from '../../../../redux/State/clientSlice/sessionSlice';

import './EditInsuranceAuthorization/index.scss';

interface RowRendererProperties {
	data: ActiveAuthorizationProperties[];
}

export const headersBCBA: HeaderProperties[] = [
	{ id: '1', name: 'Type' },
	{ id: '2', name: 'Start date' },
	{ id: '3', name: 'End date' },
	{ id: '4', name: 'Status' },
	{ id: '5', name: 'Session' },
	{ id: '6', name: 'Total' },
	{ id: '7', name: 'Weekly' },
	{ id: '8', name: 'Frequency' },
];

export const headersAdmin: HeaderProperties[] = [
	{ id: '1', name: '' },
	{ id: '2', name: 'Type' },
	{ id: '3', name: 'Start date' },
	{ id: '4', name: 'End date' },
	{ id: '5', name: 'Status' },
	{ id: '6', name: 'Session' },
	{ id: '7', name: 'Total' },
	{ id: '8', name: 'Weekly' },
	{ id: '9', name: 'Frequency' },
];

interface InsuranceAuthorizationActionsProps {
	row: ActiveAuthorizationProperties;
}

export const StatusWithTemporaryIndicator: FC<{
	endDate: string;
	isTemporary: boolean;
}> = ({ endDate, isTemporary }) =>
	isTemporary ? <Badge label="Temporary" type="primary" sizeType="sm" /> : <ExpireInfo endDate={endDate} />;

export const InsuranceAuthorizationActions = ({ row }: InsuranceAuthorizationActionsProps) => {
	const [openActions, setOpenActions] = useState<boolean>(false);
	const EDIT = useCheckPermission(PERMISSIONS.INSURANCE_AUTHORIZATION.EDIT);
	const DELETE = useCheckPermission(PERMISSIONS.INSURANCE_AUTHORIZATION.DELETE);
	const CREATE = useCheckPermission(PERMISSIONS.INSURANCE_AUTHORIZATION.CREATE);

	const toggleActionsHandler = (event: MouseEvent<SVGSVGElement>) => {
		event.stopPropagation();
		setOpenActions(!openActions);
	};

	const closeActionsHandler = () => setOpenActions(false);

	return (
		<div className="insuranceAuthorizationActions" onMouseLeave={closeActionsHandler}>
			<div className="icon">
				<InfoSvg onClick={toggleActionsHandler} />
				<div style={{ display: openActions ? 'flex' : 'none' }} className="adminActions">
					{row.isTemporary && EDIT.permissionGranted && (
						<div className="actionRow">
							<ActivateAuthorization authorizationData={row} />
						</div>
					)}
					{row.isEditable && EDIT.permissionGranted && (
						<div className="actionRow">
							<EditAuthorization authorizationData={row} />
						</div>
					)}
					{DELETE.permissionGranted && (
						<div className="actionRow">
							<DeleteAuthorization authorizationData={row} />
						</div>
					)}
					{CREATE.permissionGranted && (
						<div className="actionRow">
							<CopyAuthorization authorizationData={row} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export const TableBodyAdmin: FC<RowRendererProperties> = ({ data }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const sessionTypes = useSelector((state) => state.session.sessionTypes);

	const findTypeBackgroundColor = (typeName: string) => {
		const type = sessionTypes.find((x) => x.name === typeName);
		if (!type) return findEventColor(1);

		return findEventColor(type.id);
	};

	const findTypeColor = (typeName: string) => {
		const type = sessionTypes.find((x) => x.name === typeName);
		if (!type) return findEventIconColor(1);

		return findEventIconColor(type.id);
	};

	useEffect(() => {
		dispatch(getSessionTypes({}));
	}, [dispatch]);

	if (!data?.length) {
		return <TableNoData spanScope={headersAdmin.length} />;
	}

	return (
		<Body>
			{data.map((row, index) => {
				const detailsCount = row.authorizationDetails.length;

				return row.authorizationDetails.map((detail, detailIndex) => (
					<CustomTableRow key={`${index}-${detailIndex}`} className="insuranceTableRow">
						{detailIndex === 0 && (
							<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
								<InsuranceAuthorizationActions row={row} />
							</RowTableCell>
						)}
						{detailIndex === 0 && (
							<>
								<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
									<Text title={row.type.name} type="psm" fontWeight="medium" />
								</RowTableCell>
								<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
									<Text title={dayjs(row.startDate).format('MM/DD/YYYY')} type="psm" />
								</RowTableCell>
								<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
									<Text title={dayjs(row.endDate).format('MM/DD/YYYY')} type="psm" />
								</RowTableCell>
								<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
									<StatusWithTemporaryIndicator endDate={row.endDate} isTemporary={row.isTemporary} />
								</RowTableCell>
							</>
						)}
						<RowTableCell style={{ borderLeft: `1px solid rgba(0, 0, 0, 0.08)` }}>
							<Text
								title={detail.sessionType.name}
								type="psm"
								fontWeight="medium"
								color={findTypeColor(detail.sessionType.name)}
								style={{
									background: findTypeBackgroundColor(detail.sessionType.name),
									padding: '4px 8px',
									borderRadius: '999px',
									display: 'inline-block',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '200px',
								}}
							/>
						</RowTableCell>
						<RowTableCell>
							<Text title={`${detail.totalHours} h`} type="psm" style={{ whiteSpace: 'nowrap' }} />
						</RowTableCell>
						<RowTableCell>
							{row.type.id !== 1 && detail.hoursPerWeek ? (
								<Text title={`${detail.hoursPerWeek} h`} type="psm" style={{ whiteSpace: 'nowrap' }} />
							) : (
								<Text title="-" type="psm" color="rgba(0, 0, 0, 0.36)" />
							)}
						</RowTableCell>
						<RowTableCell>
							<Text title={detail.frequency.name} type="psm" style={{ whiteSpace: 'nowrap' }} />
						</RowTableCell>
					</CustomTableRow>
				));
			})}
		</Body>
	);
};

export const TableBodyBCBA: FC<RowRendererProperties> = ({ data }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const sessionTypes = useSelector((state) => state.session.sessionTypes);

	const findTypeBackgroundColor = (typeName: string) => {
		const type = sessionTypes.find((x) => x.name === typeName);
		if (!type) return findEventColor(1);

		return findEventColor(type.id);
	};

	const findTypeColor = (typeName: string) => {
		const type = sessionTypes.find((x) => x.name === typeName);
		if (!type) return findEventIconColor(1);

		return findEventIconColor(type.id);
	};

	useEffect(() => {
		dispatch(getSessionTypes({}));
	}, [dispatch]);

	if (!data?.length) {
		return <TableNoData spanScope={headersBCBA.length} />;
	}

	return (
		<Body>
			{data.map((row, index) => {
				const detailsCount = row.authorizationDetails.length;

				return row.authorizationDetails.map((detail, detailIndex) => (
					<CustomTableRow key={`${index}-${detailIndex}`} className="insuranceTableRow">
						{detailIndex === 0 && (
							<>
								<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
									<Text title={row.type.name} type="psm" fontWeight="medium" />
								</RowTableCell>
								<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
									<Text title={dayjs(row.startDate).format('MM/DD/YYYY')} type="psm" />
								</RowTableCell>
								<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
									<Text title={dayjs(row.endDate).format('MM/DD/YYYY')} type="psm" />
								</RowTableCell>
								<RowTableCell rowSpan={detailsCount} style={{ borderRight: `1px solid rgba(0, 0, 0, 0.08)` }}>
									<StatusWithTemporaryIndicator endDate={row.endDate} isTemporary={row.isTemporary} />
								</RowTableCell>
							</>
						)}
						<RowTableCell style={{ borderLeft: `1px solid rgba(0, 0, 0, 0.08)` }}>
							<Text
								title={detail.sessionType.name}
								type="psm"
								fontWeight="medium"
								color={findTypeColor(detail.sessionType.name)}
								style={{
									background: findTypeBackgroundColor(detail.sessionType.name),
									padding: '4px 8px',
									borderRadius: '999px',
									display: 'inline-block',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '200px',
								}}
							/>
						</RowTableCell>
						<RowTableCell>
							<Text title={`${detail.totalHours} h`} type="psm" />
						</RowTableCell>
						<RowTableCell>
							{detail.sessionType.name.includes('Assessment') && detail.hoursPerWeek ? (
								<Text title={`${detail.hoursPerWeek.toFixed(2)} h`} type="psm" />
							) : (
								<Text title="-" type="psm" color="rgba(0, 0, 0, 0.36)" />
							)}
						</RowTableCell>
					</CustomTableRow>
				));
			})}
		</Body>
	);
};
